import type { DraftEmissionValueItem } from '@api-client/api'
import { roundEmissionValue } from '@helpers/emissions'
import { get, sum } from 'lodash'

import type {
	EmissionResults,
	EnergyFormValues,
	FormValues,
	RawMaterialPackagingFormValues,
	TransportFormValues,
} from '../Calculator'
import type { Data } from './transform'

function calculateEmissionValue(
	optionsdata: Data,
	{
		category,
		subcategory,
		subsubcategory,
		emissionfactor,
		value,
	}: {
		category: string
		subcategory: string
		subsubcategory: string
		emissionfactor: string
		value: number | null
	}
) {
	const emissionvalue = get(
		optionsdata,
		`${category}.${subcategory}.${subsubcategory}.${emissionfactor}`,
		0
	) as number
	return emissionvalue * (value || 0)
}

function calculateRawMaterialPackagingEmissionValue(
	optionsdata: Data,
	{
		category,
		subcategory,
		subsubcategory,
		emissionfactor,
		weight,
		transportcategory,
		transportsubcategory,
		transportsubsubcategory,
		transportemissionfactor,
		distance,
	}: RawMaterialPackagingFormValues
) {
	const rawmaterialsvalue = calculateEmissionValue(optionsdata, {
		category,
		subcategory,
		subsubcategory,
		emissionfactor,
		value: weight ? Number(weight) / 1000 : 0, // convert g to kg
	})

	const transportvalue = calculateEmissionValue(optionsdata, {
		category: transportcategory,
		subcategory: transportsubcategory,
		subsubcategory: transportsubsubcategory,
		emissionfactor: transportemissionfactor,
		value: Number(distance) * (weight ? Number(weight) / 1000 : 0),
	})

	return rawmaterialsvalue + transportvalue
}

function calculateTotalEmissions(
	optionsdata: Data,
	{
		rawmaterial,
		packaging,
		energy,
		transport,
		transportBetweenSites,
	}: FormValues
): EmissionResults {
	const rawmaterialValues = rawmaterial?.map(item =>
		calculateRawMaterialPackagingEmissionValue(optionsdata, item)
	)
	const packagingValues = packaging?.map(item =>
		calculateRawMaterialPackagingEmissionValue(optionsdata, item)
	)
	const energyValues = energy?.map(item =>
		calculateEmissionValue(optionsdata, { ...item, value: Number(item.amount) })
	)
	const transportValues = transport?.map(item =>
		calculateEmissionValue(optionsdata, {
			...item,
			value: Number(item.distance),
		})
	)
	const transportBetweenSitesValues = transportBetweenSites?.map(item =>
		calculateEmissionValue(optionsdata, {
			...item,
			value:
				Number(item.distance) * (item.weight ? Number(item.weight) / 1000 : 0),
		})
	)

	return {
		material: roundEmissionValue(sum(rawmaterialValues) + sum(packagingValues)),
		production: roundEmissionValue(
			sum(energyValues) + sum(transportBetweenSitesValues)
		),
		distribution: roundEmissionValue(sum(transportValues)),
	}
}

export interface EmissionBreakdownItems {
	materialAcquisitionAndPreprocessing: DraftEmissionValueItem[]
	production: DraftEmissionValueItem[]
	distributionAndStorage: DraftEmissionValueItem[]
}

function formatPath(...parts: readonly string[]): string {
	return parts.filter(p => p && p !== 'NaN').join(' / ')
}

function rawMaterialPackagingItems(
	optionsdata: Data,
	row: RawMaterialPackagingFormValues
): DraftEmissionValueItem[] {
	const items: DraftEmissionValueItem[] = []

	const materialValue = calculateEmissionValue(optionsdata, {
		category: row.category,
		subcategory: row.subcategory,
		subsubcategory: row.subsubcategory,
		emissionfactor: row.emissionfactor,
		value: row.weight ? Number(row.weight) / 1000 : 0,
	})
	if (materialValue > 0) {
		items.push({
			description: `${formatPath(row.subcategory, row.subsubcategory, row.emissionfactor)} (${row.weight}g)`,
			value: roundEmissionValue(materialValue),
		})
	}

	const transportValue = calculateEmissionValue(optionsdata, {
		category: row.transportcategory,
		subcategory: row.transportsubcategory,
		subsubcategory: row.transportsubsubcategory,
		emissionfactor: row.transportemissionfactor,
		value: Number(row.distance) * (row.weight ? Number(row.weight) / 1000 : 0),
	})
	if (transportValue > 0) {
		items.push({
			description: `Transport: ${formatPath(row.transportsubcategory, row.transportsubsubcategory, row.transportemissionfactor)} (${row.weight}g × ${row.distance}km)`,
			value: roundEmissionValue(transportValue),
		})
	}

	return items
}

function energyItem(
	optionsdata: Data,
	row: EnergyFormValues
): DraftEmissionValueItem | undefined {
	const value = calculateEmissionValue(optionsdata, {
		...row,
		value: Number(row.amount),
	})
	if (value <= 0) {
		return undefined
	}
	return {
		description: `${formatPath(row.subcategory, row.subsubcategory, row.emissionfactor)} (${row.amount} kWh)`,
		value: roundEmissionValue(value),
	}
}

function transportItem(
	optionsdata: Data,
	row: TransportFormValues,
	// Keep in sync with calculateTotalEmissions: inbound (transportBetweenSites) multiplies distance by
	// weight in kg; outbound (transport) uses distance only because its EFs are pre-weighted.
	multiplyByWeight: boolean
): DraftEmissionValueItem | undefined {
	const weightKg = row.weight ? Number(row.weight) / 1000 : 0
	const value = calculateEmissionValue(optionsdata, {
		...row,
		value: multiplyByWeight
			? Number(row.distance) * weightKg
			: Number(row.distance),
	})
	if (value <= 0) {
		return undefined
	}
	const detail = multiplyByWeight
		? `${row.weight}g × ${row.distance}km`
		: `${row.distance}km`
	return {
		description: `${formatPath(row.subcategory, row.subsubcategory, row.emissionfactor)} (${detail})`,
		value: roundEmissionValue(value),
	}
}

function buildEmissionBreakdownItems(
	optionsdata: Data,
	{
		rawmaterial,
		packaging,
		energy,
		transport,
		transportBetweenSites,
	}: FormValues
): EmissionBreakdownItems {
	const materialAcquisitionAndPreprocessing: DraftEmissionValueItem[] = [
		...(rawmaterial ?? []).flatMap(row =>
			rawMaterialPackagingItems(optionsdata, row)
		),
		...(packaging ?? []).flatMap(row =>
			rawMaterialPackagingItems(optionsdata, row)
		),
	]

	const production: DraftEmissionValueItem[] = [
		...(energy ?? [])
			.map(row => energyItem(optionsdata, row))
			.filter((it): it is DraftEmissionValueItem => it !== undefined),
		...(transportBetweenSites ?? [])
			.map(row => transportItem(optionsdata, row, true))
			.filter((it): it is DraftEmissionValueItem => it !== undefined),
	]

	const distributionAndStorage: DraftEmissionValueItem[] = (transport ?? [])
		.map(row => transportItem(optionsdata, row, false))
		.filter((it): it is DraftEmissionValueItem => it !== undefined)

	return {
		materialAcquisitionAndPreprocessing,
		production,
		distributionAndStorage,
	}
}

export {
	buildEmissionBreakdownItems,
	calculateTotalEmissions,
	roundEmissionValue,
	sum,
}
